/**
 * PWA helpers (PR #90).
 *
 * - Registra service worker em produção (não em dev — Vite tem HMR)
 * - Captura evento `beforeinstallprompt` pra trigger custom de install
 * - Detecta se app está rodando como PWA standalone
 * - Persistência da escolha do user (não mostrar prompt de novo se dispensou)
 */

const STORAGE_KEY = "acordei.pwa.dispensou";

/** Captured before install prompt — usado pra trigger manual via UI. */
let _deferredPrompt: BeforeInstallPromptEvent | null = null;

// TypeScript não define BeforeInstallPromptEvent globalmente
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

/** True se app está rodando como PWA instalada (standalone). */
export function rodandoComoStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/** True se navigator suporta SW (todos browsers modernos suportam). */
export function swSuportado(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

/** Registra service worker. Só em produção (não em dev — HMR conflita). */
export async function registrarServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!swSuportado()) return null;
  if (import.meta.env?.DEV) {
    console.info("[pwa] dev mode — service worker desabilitado");
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });
    console.info("[pwa] service worker registrado:", registration.scope);
    return registration;
  } catch (e) {
    console.error("[pwa] falha ao registrar SW:", e);
    return null;
  }
}

/** Inicializa captura do beforeinstallprompt — chamar 1x no main.tsx. */
export function inicializarInstallPrompt(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _deferredPrompt = e as BeforeInstallPromptEvent;
    console.info("[pwa] install prompt capturado, disponível pra trigger");
  });
  window.addEventListener("appinstalled", () => {
    _deferredPrompt = null;
    console.info("[pwa] app instalado com sucesso");
  });
}

/** Dispara prompt de install nativo do browser. Retorna outcome. */
export async function dispararInstallPrompt(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!_deferredPrompt) return "unavailable";
  await _deferredPrompt.prompt();
  const { outcome } = await _deferredPrompt.userChoice;
  _deferredPrompt = null;
  return outcome;
}

/** True se há prompt capturado pronto pra trigger. */
export function promptDisponivel(): boolean {
  return _deferredPrompt !== null;
}

/** User dispensou o banner — persiste em localStorage pra não reaparecer. */
export function marcarDispensado(): void {
  try {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  } catch {
    // localStorage indisponível (private mode) — ok, mostra de novo na próxima sessão
  }
}

/** True se user dispensou nas últimas 7 dias. */
export function foiDispensadoRecentemente(): boolean {
  try {
    const ts = localStorage.getItem(STORAGE_KEY);
    if (!ts) return false;
    const diff = Date.now() - parseInt(ts, 10);
    return diff < 7 * 24 * 60 * 60 * 1000; // 7 dias
  } catch {
    return false;
  }
}
